import { useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import EditPage from '@/components/shared/Layouts/EditPage/EditPage';
import type { AllocationGroup } from '@/components/shared/Layouts/EditPage/EditPage.types';
import { useOrganizationId } from '@/contexts/OrganizationContext';
import { useUsersAutocomplete } from '@/hooks/autocomplete/useUsersAutocomplete';
import { useAllocationGroupUserMembership } from '@/hooks/useAllocationGroupUserMembership';
import { useAllocationGroups } from '@/hooks/useAllocationsGroup';
import { useManageAllocationGroupUsers } from '@/hooks/useManageAllocationGroupUsers';
import { useSnackbar } from '@/hooks/useSnackbar';
import { DEFAULT_PAGE_SIZE, useTablePaginationState } from '@/hooks/useTablePaginationState';
import { useUsersByAllocationGroup } from '@/hooks/useUsersByAllocationGroup';
import { ROUTES } from '@/routes';

interface LocationState {
  allocationGroup?: AllocationGroup;
  returnSearchParams?: string;
}

function EditAllocationGroup() {
  const { id } = useParams<{ id: string }>();
  const allocationGroupId = id ? parseInt(id, 10) : undefined;
  const { organizationId } = useOrganizationId();
  const location = useLocation();
  const snackbar = useSnackbar();
  const [debouncedValue, setDebouncedValue] = useState<string>('');

  const { pagination, setPagination } = useTablePaginationState({
    defaultPageSize: DEFAULT_PAGE_SIZE,
  });

  const { allocationsGroup } = useAllocationGroups();

  // Get allocation group from location state first, then fall back to fetching from hook
  const state = location.state as LocationState | null;
  const allocationGroup =
    state?.allocationGroup ??
    (allocationsGroup?.items && allocationGroupId ? (allocationsGroup.items.find((ag) => ag.id === allocationGroupId) ?? null) : null);

  const {
    data: usersGroupData,
    isLoading,
    isRefetching,
    error,
  } = useUsersByAllocationGroup(allocationGroupId, pagination.pageIndex + 1, pagination.pageSize);

  const { items: users, totalCount } = usersGroupData || {};

  const {
    addUsers,
    removeUser,
    removeUsers,
    isMutating: isManagingUsers,
    removeUserMutation,
    removeUsersBatchMutation,
  } = useManageAllocationGroupUsers(snackbar);

  const usersAutocompleteQuery = useUsersAutocomplete({
    organizationId,
    filter: debouncedValue,
    enabled: !isManagingUsers,
  });

  const usersIdsForAutocomplete =
    usersAutocompleteQuery.data?.map((u) => u.email).filter((email): email is string => email !== null && email !== undefined) ?? [];

  const membershipQuery = useAllocationGroupUserMembership({
    organizationId,
    groupId: allocationGroupId ?? 0,
    userIds: usersIdsForAutocomplete,
    enabled: !isManagingUsers && usersIdsForAutocomplete.length > 0,
  });

  const usersList =
    users?.map((u) => ({
      ...u,
      email: (u.email || u.userId) ?? null,
    })) ?? [];

  const now = new Date();
  const currentDate = `${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}${now.getFullYear()}`;

  return (
    <>
      <EditPage
        route={ROUTES.ALLOCATIONS}
        group={allocationGroup}
        organizationId={organizationId}
        isManagingUsers={isManagingUsers}
        pagination={{ state: pagination, onChange: setPagination }}
        breadcrumbItems={{
          prevPathLabel: 'Inventory Allocations',
          currentPathLabel: 'Manage Inventory Allocation',
          currentPath: `/allocations/manage/${allocationGroupId}`,
        }}
        sectionTitle={{
          testId: 'user-details-title',
          title: allocationGroup?.name || 'Allocation Group Name',
        }}
        autocompleteProps={{
          usersData: usersAutocompleteQuery,
          membershipData: membershipQuery,
          filter: debouncedValue,
          onFilteredSearch: setDebouncedValue,
          disabled: !allocationGroupId || isManagingUsers,
          onAddUsers: (groupId, userIds, group) => {
            const allocationGroup = group as AllocationGroup;
            return addUsers(groupId, userIds, {
              allocationGroupName: allocationGroup.name ?? null,
              isActive: allocationGroup?.isActive,
            });
          },
        }}
        error={error}
        table={{
          testId: 'allocation-users-table',
          ariaLabel: 'Allocation group users list',
          noDataMessage: 'No users assigned.',
          data: usersList,
          totalCount: totalCount ?? 0,
          onRemoveSingleUser: (groupId, user) => removeUser(groupId, user.userId!),
          onRemoveUsersInBatch: (groupId, users) =>
            removeUsers(
              groupId,
              users.map((u) => ({ userId: u.userId! })),
            ),
          isRefetching,
          isLoading,
          removeUserMutation,
          removeUsersBatchMutation,
        }}
        exportConfig={{
          url: `/api/organizations/${organizationId}/allocation-groups/${allocationGroupId}/export-users`,
          fileName: `AG_${allocationGroup?.name}_Users_${currentDate}.xlsx`,
        }}
      />
    </>
  );
}

export default EditAllocationGroup;
